import { formatDate, parseISO } from "date-fns"
import { cn } from "design-system/utils"
import { CircleCheck, MessageSquareText, RotateCcw } from "lucide-react"
import { getConversationTitle } from "modules/conversations/lib/getConversationTitle"
import { useTranslation } from "react-i18next"
import { CustomerIssueResponseDataSchema } from "schema"
import { z } from "zod"

type Issue = z.infer<typeof CustomerIssueResponseDataSchema>
type Update = Issue["updates"][number]

type TimelineNode =
  | {
      kind: "conversation"
      conversationId: string
      conversation: NonNullable<Update["conversation"]>
    }
  | { kind: "update"; update: Update }

type Props = {
  issue: Issue
  onConversationClick: (conversationId: string) => void
}

export function IssueUpdatesTimeline({ issue, onConversationClick }: Props) {
  const { t } = useTranslation()
  const nodes = buildTimelineNodes(issue.updates)

  return (
    <div className="flex flex-col">
      {nodes.map((node, index) => {
        const isFirst = index === 0
        const isLast = index === nodes.length - 1
        const content = (
          <div className="flex gap-2 pr-1">
            <div className="flex w-3 shrink-0 flex-col items-center">
              <div className={cn("h-2.5 w-px", !isFirst && "bg-border")} />
              <div
                className={cn(
                  "size-2 shrink-0 rounded-full ring-2 ring-background",
                  node.kind === "conversation" && "bg-muted-foreground/40",
                  node.kind === "update" && getUpdateColors(node.update).bullet,
                )}
              />
              <div className={cn("w-px grow", !isLast && "bg-border")} />
            </div>
            <div className="flex min-w-0 flex-1 items-start py-1.5 text-sm leading-snug text-foreground">
              {node.kind === "conversation" ? (
                <div className="flex w-full min-w-0 items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                    <MessageSquareText size={13} />
                    {getConversationTitle(node.conversation, t)}
                  </span>
                </div>
              ) : (
                <IssueUpdateContent update={node.update} />
              )}
            </div>
          </div>
        )

        if (node.kind === "conversation") {
          return (
            <div key={`conversation-${node.conversationId}-${index}`} className="-mx-2">
              {!isFirst && (
                <div className="ml-2 flex h-2.5 w-3 justify-center">
                  <div className="w-px bg-border" />
                </div>
              )}
              <button
                type="button"
                className="block w-full cursor-pointer rounded-sm px-2 text-left transition-colors hover:bg-muted"
                onClick={() => onConversationClick(node.conversationId)}
              >
                {content}
              </button>
            </div>
          )
        }

        return <div key={`update-${node.update.id}`}>{content}</div>
      })}
    </div>
  )
}

function IssueUpdateContent({ update }: { update: Update }) {
  const { t } = useTranslation()
  const status = getUpdateStatus(update)

  return (
    <div className="flex w-full min-w-0 flex-col gap-1">
      <div className="flex items-start justify-between gap-3">
        {status ? (
          <span className={cn("flex items-center gap-2", getUpdateColors(update).text)}>
            {update.isReopening ? <RotateCcw size={13} /> : <CircleCheck size={13} />}
            {update.employee
              ? t(`support.entities.update.${status}By`, { employee: update.employee.name })
              : t(`support.entities.update.${status}`)}
          </span>
        ) : (
          <span className="whitespace-pre-wrap">{update.summary}</span>
        )}
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatDate(parseISO(update.createdAt), "Pp")}
        </span>
      </div>
      {status && update.summary && <p className="whitespace-pre-wrap">{update.summary}</p>}
    </div>
  )
}

function getUpdateColors(update: Update) {
  const isAutomatic = update.employeeId === null

  if (update.isReopening && isAutomatic) {
    return { bullet: "bg-testing-1", text: "text-testing-1" }
  }

  if (update.isResolution && update.resolutionStatus === "Resolved" && isAutomatic) {
    return { bullet: "bg-success", text: "text-success" }
  }

  if (update.isReopening || update.isResolution) {
    return { bullet: "bg-muted-foreground", text: "text-muted-foreground" }
  }

  return { bullet: "bg-support-secondary", text: "text-foreground" }
}

function getUpdateStatus(update: Update) {
  if (update.isReopening) return "reopened"
  if (!update.isResolution) return null

  return update.resolutionStatus === "Closed" ? "closed" : "resolved"
}

function buildTimelineNodes(updates: Issue["updates"]) {
  const nodes: TimelineNode[] = []
  let previousConversationId: string | null = null

  const orderedUpdates = [...updates].sort((left, right) => {
    const timeDifference = Date.parse(left.createdAt) - Date.parse(right.createdAt)

    return timeDifference || left.sequence - right.sequence
  })

  for (const update of orderedUpdates) {
    if (
      update.conversationId &&
      update.conversation &&
      update.conversationId !== previousConversationId
    ) {
      nodes.push({
        kind: "conversation",
        conversationId: update.conversationId,
        conversation: update.conversation,
      })
    }

    nodes.push({ kind: "update", update })
    previousConversationId = update.conversationId
  }

  return nodes
}
